import PropTypes from "prop-types";
import { createContext, useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { resetState } from "../state/actions";
import { useQueryClient } from "@tanstack/react-query";
import useInterval from "../hooks/useInterval";
import { refreshTokenApi } from "../api/auth";

import {
  getSessionStorage,
  setSessionStorage,
  removeSessionStorage,
} from "../util/sessionStorage";
import { getExpTimeSeconds } from "../util/Helpers";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(getSessionStorage("auth_data") || {});
  const [rememberMe, setRememberMe] = useState(
    getSessionStorage("auth_remember") || false,
  );
  const [sessionMessage, setSessionMessage] = useState(null);

  const isLoading = useRef(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const getAuth = useCallback(() => {
    return auth;
  }, [auth]);

  const updateAuth = useCallback(
    (value = {}) => {
      setAuth(value);
      setSessionStorage("auth_data", value);
    },
    [setAuth],
  );

  const setAuthLoading = useCallback((state) => {
    isLoading.current = state;
  }, []);

  const signIn = useCallback(
    ({ token, expires, refresh_token }, rememberMe = false) => {
      updateAuth({ token, expires, refresh_token });
      if (rememberMe) {
        setRememberMe(true);
        setSessionStorage("auth_remember", true);
      }
    },
    [updateAuth],
  );

  const signOut = useCallback(
    (reason) => {
      updateAuth({});
      setRememberMe(false);
      removeSessionStorage("auth_data");
      removeSessionStorage("auth_remember");
      removeSessionStorage("pdf_edit");
      dispatch(resetState());
      queryClient.clear();
      setSessionMessage(reason || null);
    },
    [dispatch, queryClient, updateAuth],
  );

  const clearSessionMessage = useCallback(() => setSessionMessage(null), []);

  const isAuthenticated = useCallback(() => {
    const { token, refresh_token, expires } = getAuth();
    return !!(token && refresh_token && expires);
  }, [getAuth]);

  const isExpired = useCallback(() => {
    const { expires } = getAuth();
    return expires && new Date(expires) - new Date() < 1000 * 10;
  }, [getAuth]);

  const getExpSeconds = useCallback(() => {
    if (!isAuthenticated()) return null;
    const { expires } = getAuth();
    if (!expires) return null;
    return getExpTimeSeconds(expires);
  }, [getAuth, isAuthenticated]);

  const refreshToken = useCallback(
    (signal) => {
      const { refresh_token } = getAuth();
      if (!rememberMe || !refresh_token) {
        signOut("Your session has expired. Please log in again.");
        return;
      }
      setAuthLoading(true);
      refreshTokenApi(refresh_token, signal)
        .then(({ data = {} } = {}) => {
          updateAuth(data);
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            return;
          }
          console.error("Refresh Token Error", error);
          signOut("Your session has expired. Please log in again.");
        })
        .finally(() => setAuthLoading(false));
    },
    [getAuth, setAuthLoading, rememberMe, signOut, updateAuth],
  );

  useInterval(refreshToken, getExpSeconds());

  useEffect(() => {
    if (isExpired() && !isLoading.current) {
      const controller = new AbortController();
      refreshToken(controller.signal);
      return () => controller.abort();
    }
  }, [isExpired, refreshToken]);

  useEffect(() => {
    const controller = new AbortController();
    const onVisibilityChange = () => {
      if (isExpired() && !isLoading.current) {
        refreshToken(controller.signal);
      }
    };
    window.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      controller.abort();
      window.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isExpired, refreshToken]);

  const contextValue = useMemo(
    () => ({
      rememberMe,
      get isLoading() {
        return isLoading.current;
      },
      isAuthenticated,
      isExpired,
      setAuthLoading,
      getAuth,
      signIn,
      signOut,
      updateAuth,
      sessionMessage,
      clearSessionMessage,
    }),
    [
      rememberMe,
      isLoading,
      isAuthenticated,
      isExpired,
      setAuthLoading,
      getAuth,
      signIn,
      signOut,
      updateAuth,
      sessionMessage,
      clearSessionMessage,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export default AuthContext;
